import { useEffect, useState } from "react";
import { apiRequest } from "../services/apiClient";

export default function AdminAccount({ user }) {
  const [form, setForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  function update(key, value) { setForm(prev => ({ ...prev, [key]: value })); }

  async function submit(event) {
    event.preventDefault();
    setMessage("");
    if (form.newPassword !== form.confirmPassword) { setMessage("New passwords do not match."); return; }
    setSaving(true);
    try {
      const result = await apiRequest("/admin/account/password", { method: "POST", body: JSON.stringify({ currentPassword: form.currentPassword, newPassword: form.newPassword }) });
      setMessage(result.message);
      setForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) { setMessage(error.message); }
    finally { setSaving(false); }
  }

  return <div style={{ width: "100%", maxWidth: "none" }}>
    <style>{".admin-account-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .admin-account-wide { grid-column: 1 / -1; } @media (max-width: 760px) { .admin-account-grid { grid-template-columns: 1fr; } .admin-account-wide { grid-column: auto; } }"}</style>
    <h2 style={{ color: "#fff", margin: "0 0 8px" }}>Admin Account</h2>
    <p style={{ color: "rgba(255,255,255,0.45)", fontSize: 13 }}>Signed in as {user?.email || "admin"}. Change your admin password securely.</p>
    <form className="adm-card" onSubmit={submit}>
      <div className="adm-card-header"><span className="adm-card-title">Change password</span></div>
      <div className="admin-account-grid" style={{ padding: 22, display: "grid", gap: 14 }}>
        <Field label="Current password" value={form.currentPassword} onChange={value => update("currentPassword", value)} />
        <Field label="New password" value={form.newPassword} onChange={value => update("newPassword", value)} />
        <Field label="Confirm new password" value={form.confirmPassword} onChange={value => update("confirmPassword", value)} />
        <p className="admin-account-wide" style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, margin: 0 }}>Use at least 10 characters. The password is stored as a secure bcrypt hash in MySQL.</p>
        <div className="admin-account-wide" style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <button className="adm-btn adm-btn-primary" disabled={saving}>{saving ? "Updating..." : "Update password"}</button>
          {message && <span style={{ color: message.includes("successfully") ? "#4ade80" : "#f87171", fontSize: 13 }}>{message}</span>}
        </div>
      </div>
    </form>
    <AdminAccountsList currentEmail={user?.email} />
  </div>;
}

function AdminAccountsList({ currentEmail }) {
  const [admins, setAdmins] = useState(null);
  const [error, setError] = useState("");
  const [newAdmin, setNewAdmin] = useState({ email: "", displayName: "", password: "" });
  const [creating, setCreating] = useState(false);

  function loadAdmins() {
    apiRequest("/admin/admins").then(setAdmins).catch(err => setError(err.message));
  }

  useEffect(loadAdmins, []);

  async function createAdmin(event) {
    event.preventDefault();
    setError("");
    setCreating(true);
    try {
      await apiRequest("/admin/admins", { method: "POST", body: JSON.stringify(newAdmin) });
      setNewAdmin({ email: "", displayName: "", password: "" });
      loadAdmins();
    } catch (err) { setError(err.message); }
    finally { setCreating(false); }
  }

  async function removeAdmin(email) {
    if (!window.confirm(`Remove admin access for ${email}?`)) return;
    setError("");
    try {
      await apiRequest(`/admin/admins/${encodeURIComponent(email)}`, { method: "DELETE" });
      loadAdmins();
    } catch (err) { setError(err.message); }
  }

  return <div className="adm-card" style={{ marginTop: 24 }}>
    <div className="adm-card-header"><span className="adm-card-title">Admin accounts</span></div>
    <div style={{ padding: 22, display: "grid", gap: 16 }}>
      {error && <span style={{ color: "#f87171", fontSize: 13 }}>{error}</span>}
      {!admins && <span style={{ color: "rgba(255,255,255,0.45)", fontSize: 13 }}>Loading...</span>}
      {admins && <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ color: "rgba(255,255,255,0.45)", textAlign: "left" }}>
            <th style={{ padding: "6px 8px" }}>Email</th>
            <th style={{ padding: "6px 8px" }}>Name</th>
            <th style={{ padding: "6px 8px" }}>Created</th>
            <th style={{ padding: "6px 8px" }}></th>
          </tr>
        </thead>
        <tbody>
          {admins.map(admin => <tr key={admin.email} style={{ borderTop: "1px solid rgba(255,255,255,0.08)", color: "#fff" }}>
            <td style={{ padding: "8px" }}>{admin.email}{admin.email === currentEmail && <span style={{ color: "rgba(255,255,255,0.4)" }}> (you)</span>}</td>
            <td style={{ padding: "8px" }}>{admin.display_name || "-"}{admin.isSuperAdmin && <span className="adm-badge adm-badge-done" style={{ marginLeft: 8 }}>Super Admin</span>}</td>
            <td style={{ padding: "8px" }}>{admin.created_at ? new Date(admin.created_at).toLocaleDateString() : "-"}</td>
            <td style={{ padding: "8px" }}>
              {admin.email !== currentEmail && !admin.isSuperAdmin && <button type="button" className="adm-btn" onClick={() => removeAdmin(admin.email)}>Remove</button>}
            </td>
          </tr>)}
        </tbody>
      </table>}

      <form onSubmit={createAdmin} className="admin-account-grid" style={{ display: "grid", gap: 12, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 16 }}>
        <span className="adm-card-title admin-account-wide" style={{ fontSize: 13 }}>Add new admin</span>
        <Field label="Email" type="email" value={newAdmin.email} onChange={value => setNewAdmin(prev => ({ ...prev, email: value }))} />
        <Field label="Display name" type="text" value={newAdmin.displayName} onChange={value => setNewAdmin(prev => ({ ...prev, displayName: value }))} />
        <Field label="Password" value={newAdmin.password} onChange={value => setNewAdmin(prev => ({ ...prev, password: value }))} />
        <div className="admin-account-wide"><button className="adm-btn adm-btn-primary" disabled={creating}>{creating ? "Adding..." : "Add admin"}</button></div>
      </form>
    </div>
  </div>;
}

function Field({ label, value, onChange, type = "password" }) {
  return <label style={labelStyle}><span>{label}</span><input type={type} value={value} onChange={event => onChange(event.target.value)} required style={inputStyle} /></label>;
}

const labelStyle = { display: "flex", flexDirection: "column", gap: 7, color: "rgba(255,255,255,0.65)", fontSize: 12, fontWeight: 600 };
const inputStyle = { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 6, padding: "10px 12px", color: "#fff", fontFamily: "inherit", fontSize: 14 };